import { Box, IconButton, TextField } from '@mui/material';
import React, { memo, useCallback, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { styled } from '@mui/material/styles';
import { spaces } from '../../../../../assets/styles/themes/tokens';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';

type ColorPickerProps = {
    color?: string
    onChange?: (color: string) => void
    onClear?: () => void
};

const colors = ['#F28B82', '#FBCB73', '#FFF475', '#CCFF90', '#A7FFEB', '#CBF0F8', '#AECBFA'];

const StyledColorPickerContainer = styled(HexColorPicker)({
  width: 'inherit!important',
});

const ColorPicker = ({ color, onChange, onClear }: ColorPickerProps) => {
  const [colorInputValue, setColorInputValue] = useState(color || '');

  const handleColorUpdate = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setColorInputValue(event.target.value);
  }, []);

  const handleColorChange = useCallback(() => {
    const isCorrectColor = /^#([0-9A-F]{3}){1,2}$/i.test(colorInputValue);

    if (!isCorrectColor) {
      if (onChange) {
        onChange('');
      }

      return;
    }

    if (onChange) {
      onChange(colorInputValue);
    }
  }, [colorInputValue, onChange]);

  const handleColorSelection = useCallback((currentColor) => {
    if (onChange) {
      onChange(currentColor || '');
    }
  }, [onChange]);
  
  return (
    <Box
      sx={{
        width: 270,
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.xxxs,
      }}>
      <StyledColorPickerContainer color={color || ''} onChange={onChange} />
      <TextField
        data-testid='hex-color-input'
        type='text'
        size='small' 
        id='hex-color-input' 
        variant='outlined'
        placeholder='#000000'
        value={colorInputValue}
        onChange={handleColorUpdate}
        onBlur={handleColorChange} 
        inputProps={{ maxLength: 7 }}
      />
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginY: spaces.sm,
        marginX: spaces.xs,
      }}>
        <Box
          data-testid='theme-colors' 
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: spaces.sm,
          }}
        >
          {colors.map((currentColor, index) => (
            <IconButton
              key={index}
              size='small'
              onClick={() => handleColorSelection(currentColor)}
              sx={{
                width: 18,
                height: 18,  
                backgroundColor: currentColor,
                border: currentColor === color ? '0.5px solid #fff' : 'none',
                borderRadius: '.25rem',
                boxShadow: currentColor === color ? `0 0 0 6px ${color}55` : 'none',
                '&:hover': {
                  backgroundColor: currentColor,
                },
              }}
            />
          ))}
        </Box>
        <IconButton
          data-testid='clear-color'
          size='small'
          onClick={onClear}
          sx={{
            width: 18,
            height: 18, 
          }}
        >
          <UndoOutlinedIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default memo(ColorPicker);
